import React, { useEffect, useState } from 'react';
import { DatePicker, Spin } from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { WalletOutlined, TeamOutlined, DollarOutlined } from '@ant-design/icons';
import { GetIBDashboardApi, GetIBOverviewApi } from '../../../api/request/ib/ibDashboard.js';
import TransactionData from './components/TransactionData.jsx';
import CompleteTick from '../../../assets/user/CompleteTick.jsx';
import Clock from '../../../assets/user/Clock.jsx';
import Copy from '../../../components/copy/Copy.jsx';
import AreaChart from '../../../components/charts/AreaChart.jsx';
import TableWithSkeleton from '../../../components/table/TableWithSkeleton.jsx';
import columns from '../../../columns/topScorer.jsx';
import { presets } from '../../../utils/RangePickerPresets.js';

const { RangePicker } = DatePicker;

const StatCard = ({ title, value, color, gradient, icon, onClick, prefix = '' }) => (
  <div
    className={`rounded-2xl p-3 text-center shadow-md border border-white bg-gradient-to-tl ${gradient} ${onClick ? 'cursor-pointer' : ''}`}
    onClick={onClick}
  >
    <div className="flex flex-col items-center gap-2 mb-2">
      <div className={`p-2 ${color} rounded-[10px] flex items-center justify-center`}>
        {icon}
      </div>
      <h3 className="text-xs font-bold text-gray-700 leading-5">{title}</h3>
    </div>
    <p className="text-sm font-bold text-gray-900">{prefix}{value || 0}</p>
  </div>
);

const IBDashboardMobile = () => {
  const navigate = useNavigate();
  const { userDetails } = useSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState(true);
  const [isOverViewLoading, setIsOverViewLoading] = useState(false);
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState([]);
  const [overviewRange, setOverviewRange] = useState({
    from: moment(new Date()).subtract(6, 'days').format('MM/DD/YYYY'),
    to: moment(new Date()).format('MM/DD/YYYY'),
  });

  const primaryColor = (getComputedStyle(document.documentElement).getPropertyValue('--theme'))?.trim();
  const secondary = (getComputedStyle(document.documentElement).getPropertyValue('--theme2'))?.trim();
  const currentDate = moment(new Date()).format('YYYY/MM/DD');
  const dateBefore6Days = moment(new Date()).subtract(6, 'days').format('YYYY/MM/DD');

  const getIbDashboard = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetIBDashboardApi();
      if (data?.success) {
        setData(data?.data);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const getIbOverView = async () => {
    try {
      setIsOverViewLoading(true);
      const params = {
        FromDate: overviewRange?.from,
        ToDate: overviewRange?.to,
      };
      const { data } = await GetIBOverviewApi(new URLSearchParams(params).toString());
      if (data?.success) {
        const lotData = [];
        const commissionData = [];
        const dateData = [];
        (data?.data || []).forEach((item) => {
          lotData.push(item?.lot);
          commissionData.push(item?.commission);
          dateData.push(moment(item.dateTime).format('MMM DD'));
        });
        setOverview({
          categories: dateData,
          series: [
            { name: 'Total Lot', data: lotData },
            { name: 'Total Commission', data: commissionData, prefix: '$', postfix: '' },
          ],
        });
      }
    } catch (error) {
    } finally {
      setIsOverViewLoading(false);
    }
  };

  useEffect(() => {
    getIbDashboard();
  }, []);

  useEffect(() => {
    getIbOverView();
  }, [overviewRange]);

  const disabled7DaysDate = (current, { from }) => {
    if (from) return Math.abs(current.diff(from, 'days')) >= 7;
    return false;
  };

  const handleDateChange = (value) => {
    if (value && value.length === 2) {
      setOverviewRange({
        from: value[0].format('MM/DD/YYYY'),
        to: value[1].format('MM/DD/YYYY'),
      });
      return;
    }
    setOverviewRange({ from: '', to: '' });
  };

  const Deposit = [
    { name: 'Complete', count: data?.ftdCompleted, icon: <CompleteTick color="#00727d" />, bgColor: 'bg-[#E5F1F2]', color: '#00727d' },
    { name: 'Pending', count: data?.ftdPending, icon: <Clock color="#FF9F43" />, bgColor: 'bg-[#FFF0E1]', color: '#FF9F43' },
  ];

  const KycDetail = [
    { name: 'Complete', count: data?.kycCompleted, icon: <CompleteTick color="#7367F0" />, bgColor: 'bg-[#E9E7FD]', color: '#7367F0' },
    { name: 'Pending', count: data?.kycPending, icon: <Clock color="#FF9F43" />, bgColor: 'bg-[#FFF0E1]', color: '#FF9F43' },
  ];

  const LiveAccount = [
    { name: 'Complete', count: data?.accountCompleted, icon: <CompleteTick color="#00BAD1" />, bgColor: 'bg-[#D6F4F8]', color: '#00BAD1' },
    { name: 'Pending', count: data?.accountPending, icon: <Clock color="#FF9F43" />, bgColor: 'bg-[#FFF0E1]', color: '#FF9F43' },
  ];

  const ActiveTrader = [
    { name: 'Active', count: data?.traderActive, icon: <CompleteTick color="#28C76F" />, bgColor: 'bg-[#DDF6E8]', color: '#28C76F' },
    { name: 'Inactive', count: data?.traderInactive, icon: <Clock color="#FF9F43" />, bgColor: 'bg-[#FFF0E1]', color: '#FF9F43' },
  ];

  const ibCodeBar = userDetails?.referralCode ? (
    <div className="bg-primary-light px-3 py-2.5 rounded-xl flex items-center gap-2 w-full min-w-0">
      <p className="text-sm truncate flex-1">
        <span className="font-medium">IB Code:</span> {userDetails.referralCode}
      </p>
      <Copy text={`${window?.location?.origin}/?tab=register&ref=${userDetails.referralCode}`} />
    </div>
  ) : null;

  if (isLoading) {
    return (
      <div className="space-y-5">
        {ibCodeBar}
        <div className="flex justify-center flex-col mt-[20%]">
          <Spin size="large" />
          <p className="primary text-center mt-2">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-dashboard min-h-screen space-y-5">
      {ibCodeBar}

      <div>
        <p className="text-xl font-medium">Welcome back,</p>
        <p className="text-base text-gray-600">
          {userDetails?.fullName || `${userDetails?.firstName || ''} ${userDetails?.lastName || ''}`.trim()}
        </p>
        <p className="text-sm text-gray-500 mt-1">Your progress this week is Awesome. lets keep it up!</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <StatCard
          title="Today Earning"
          value={data?.todayEarning || 0}
          prefix="$"
          color="bg-[#7367F0]"
          gradient="from-[#E9E7FD] to-[#ffff]"
          icon={<DollarOutlined className="text-white" />}
          onClick={() => navigate('/ib/commission?filter=today')}
        />
        <StatCard
          title="Today Lots"
          value={data?.todayLots || 0}
          color="bg-[#00BAD1]"
          gradient="from-[#D6F4F8] to-[#ffff]"
          icon={<WalletOutlined className="text-white" />}
          onClick={() => navigate('/ib/commission?filter=today')}
        />
        <StatCard
          title="My Team"
          value={data?.myTeam || 0}
          color="bg-[#FF9F43]"
          gradient="from-[#FFF0E1] to-[#ffff]"
          icon={<TeamOutlined className="text-white" />}
          onClick={() => navigate('/ib/my_trader_team')}
        />
      </div>

      <div className="bg-white rounded-2xl p-4 border border-gray-100">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">IB Wallet</h3>
            <p className="text-sm text-gray-500">Available IB Commission</p>
            <p className="text-2xl font-semibold primary mt-2">$ {data?.availableIBCommission || 0}</p>
          </div>
          <button
            type="button"
            className="bg-primary text-sm text-white font-semibold px-4 py-2 rounded-lg"
            onClick={() => navigate('/ib/withdraw')}
          >
            Withdraw
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <TransactionData data={Deposit} title="Deposit" />
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <TransactionData data={KycDetail} title="KYC Details" />
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <TransactionData data={LiveAccount} title="Live Account" />
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <TransactionData data={ActiveTrader} title="Active Trader" />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <div className="h-[10px] w-[10px] bg-secondary rounded-full" />
              <p className="ml-2 text-sm font-medium text-[#07111CCC]">Lot</p>
            </div>
            <div className="flex items-center">
              <div className="h-[10px] w-[10px] bg-primary rounded-full" />
              <p className="ml-2 text-sm font-medium text-[#07111CCC]">Commission</p>
            </div>
          </div>
          <RangePicker
            className="w-full border-primary py-2"
            disabledDate={disabled7DaysDate}
            onChange={handleDateChange}
            defaultValue={[dayjs(dateBefore6Days, 'YYYY/MM/DD'), dayjs(currentDate, 'YYYY/MM/DD')]}
            presets={presets}
            format="DD-MM-YYYY"
          />
        </div>

        {isOverViewLoading ? (
          <div className="flex justify-center flex-col my-10">
            <Spin size="large" />
            <p className="primary text-center mt-2">Loading...</p>
          </div>
        ) : (
          <div className="px-1 pb-2">
            <AreaChart data={overview} colors={[secondary, primaryColor]} height={260} />
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <p className="text-lg font-semibold mb-3">Top Scorer</p>
        <div className="overflow-x-auto">
          <TableWithSkeleton columns={columns} data={data?.topCommissionUsers || []} loading={false} />
        </div>
      </div>
    </div>
  );
};

export default IBDashboardMobile;
